// Pure Azure cost adapter. No React, no fetch. Types match the exporter's
// GET /service-costs response.
import { ENVIRONMENT_ORDER, sortByEnvironment } from './lib';

export interface AzureSourceStatus {
  name: string;
  asOf: string | null;
  windowStart: string;
  windowEnd: string;
  stale: boolean;
}

export interface AzureResourceCost {
  name: string;
  resourceGroup: string;
  cost: number;
  byCategory: Record<string, number>;
}

export interface AzureEnvironmentCost {
  environment: string;
  totalCost: number;
  byCategory: Record<string, number>;
  resources: AzureResourceCost[];
}

export interface ServiceCostResponse {
  service: string;
  currency?: string;
  window: { start: string; end: string };
  asOf: string | null;
  sources: AzureSourceStatus[];
  environments: AzureEnvironmentCost[];
}

export interface AzureRow {
  environment: string;
  totalCost: number;
  byCategory: Record<string, number>;
  resources: AzureResourceCost[];
}

const TOTAL_ROW_ENVIRONMENT = 'Total';

/** Environment rows via the shared sort, plus a signed Total row when more
 * than one environment is present. The Total row carries no per-resource
 * drill-down (resources: []) — that detail is only meaningful per
 * environment. */
export function toAzureRows(response: ServiceCostResponse): AzureRow[] {
  const rows: AzureRow[] = sortByEnvironment(
    response.environments.map(env => ({
      environment: env.environment,
      totalCost: env.totalCost,
      byCategory: env.byCategory,
      resources: env.resources,
    })),
  );

  if (rows.length > 1) {
    const byCategory: Record<string, number> = {};
    let totalCost = 0;
    for (const row of rows) {
      totalCost += row.totalCost;
      for (const [category, cost] of Object.entries(row.byCategory)) {
        byCategory[category] = (byCategory[category] ?? 0) + cost;
      }
    }
    rows.push({
      environment: TOTAL_ROW_ENVIRONMENT,
      totalCost,
      byCategory,
      resources: [],
    });
  }

  return rows;
}

/** Sources that are stale (cached data present but old) OR never loaded
 * (asOf: null, data genuinely absent) — callers distinguish the two, but
 * both belong in the "don't fully trust this yet" set. */
export function staleSources(response: ServiceCostResponse): AzureSourceStatus[] {
  return response.sources.filter(source => source.stale || source.asOf === null);
}

export type AzureSectionState =
  | { kind: 'hidden' }
  | { kind: 'unavailable' }
  | { kind: 'empty' }
  | { kind: 'rows'; response: ServiceCostResponse };

function isServiceCostResponse(body: unknown): body is ServiceCostResponse {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const candidate = body as Record<string, unknown>;
  return (
    typeof candidate.service === 'string' &&
    Array.isArray(candidate.environments) &&
    Array.isArray(candidate.sources) &&
    typeof candidate.window === 'object' &&
    candidate.window !== null
  );
}

/** `status: null` covers a network-level failure (fetch threw).
 * `expectedService` is the entity's kubernetes-id — a payload is only
 * trusted when `response.service` strictly equals it, so a stray or
 * misrouted payload can never render under the wrong entity. Currency is
 * checked against `USD` specifically, not just presence: the card renders a
 * hard `$`, so anything else is worse than no figure at all. */
export function azureSectionState(
  status: number | null,
  body: unknown,
  expectedService: string,
): AzureSectionState {
  if (status === 404) {
    return { kind: 'hidden' };
  }
  if (status !== 200 || !isServiceCostResponse(body)) {
    return { kind: 'unavailable' };
  }
  if (body.currency !== 'USD') {
    return { kind: 'unavailable' };
  }
  if (body.service !== expectedService) {
    return { kind: 'unavailable' };
  }
  return body.environments.length === 0
    ? { kind: 'empty' }
    : { kind: 'rows', response: body };
}

export { ENVIRONMENT_ORDER };
